// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: wallpapers/$wallpaperId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * The wallpaper catalog. Admin-managed collection of wallpaper images available for browsing and purchase. Status values: 'pending' (awaiting moderation), 'published' (publicly visible), 'declined' (moderation rejected), and 'deleted' (tombstone — set by backend when creator profile is deleted; UI filters these from public catalogs but the record persists so existing buyer library entries in users/$userId/purchases/$wallpaperId continue to resolve).
 */

export interface WallpapersRequest {
  title: string;
  imageUrl: string;
  thumbnailUrl?: string;
  category?: string;
  uploadedBy: AddressType;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  downloadCount: number | TimeOperation | IncrementOperation | TokenAmount;
  price: number | TimeOperation | IncrementOperation | TokenAmount;
  mediaType?: string;
  status: string;
  reviewedAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  reviewedBy?: AddressType;
  rejectionReason?: string;
  hidden?: boolean;
  sizeBytes?: number | TimeOperation | IncrementOperation | TokenAmount;
  categorySetAt?: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WallpapersResponse {
  title: string;
  imageUrl: string;
  thumbnailUrl?: string;
  category?: string;
  uploadedBy: string;
  createdAt: number;
  downloadCount: number;
  price: number;
  mediaType?: string;
  status: string;
  reviewedAt?: number;
  reviewedBy?: string;
  rejectionReason?: string;
  hidden?: boolean;
  sizeBytes?: number;
  categorySetAt?: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Wallpapers operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWallpapers(wallpaperId: string, data: WallpapersRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `wallpapers/${wallpaperId}`, document: doc };
}

/**
 * Two creation paths: (1) USER SUBMISSION — any authenticated user can submit a wallpaper where @user.address must equal @newData.uploadedBy, status must be 'pending', downloadCount must be 0, price must be >= 1 (SKR raw units), and title length 1-100 chars. (2) ADMIN DIRECT — ADMIN_ADDRESS can create wallpapers with status 'pending' or 'published', any price, any downloadCount, title 1-100 chars. uploadedBy is immutable after create (Address!). createdAt is immutable after create (UInt!). The optional 'hidden' field defaults to null/false on create. The optional 'sizeBytes' field stores the image file size in bytes captured at upload time and is null when not provided. The optional 'categorySetAt' field (UInt seconds) records when the wallpaper's category was last assigned; clients set it = createdAt on create. Extra fields are permitted by both create conditions. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWallpapers(wallpaperId: string, data: WallpapersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`wallpapers/${wallpaperId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Wallpapers: ${formatError(error)}`);
    return false;
  }
}

export type WallpapersRequestUpdate = Partial<WallpapersRequest>;

/**
 * Build a Wallpapers update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateWallpapers(wallpaperId: string, data: WallpapersRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `wallpapers/${wallpaperId}`, document: doc };
}

/**
 * Nine allowed paths: (1) ADMIN — ADMIN_ADDRESS may update any fields except immutable uploadedBy/createdAt; this is the path the 'move between categories' edit uses and it may freely set/change categorySetAt (no pin). (2) BACKEND DOWNLOAD-COUNT — PROJECT_VAULT_ADDRESS may update only non-moderation fields (e.g., increment downloadCount); cannot change price, uploadedBy, createdAt, or status. This branch does NOT require categorySetAt to change — a download must never bump category freshness. (3) RESUBMIT — the original uploader may resubmit a rejected wallpaper by setting status back to 'pending'; price must stay 0, downloadCount unchanged, and categorySetAt is PINNED unchanged. (4) MIGRATION — ADMIN_ADDRESS or PROJECT_VAULT_ADDRESS may backfill status on legacy wallpapers where status is null; uploadedBy/createdAt immutable. (5) BACKEND SOFT-DELETE — PROJECT_VAULT_ADDRESS may transition status to 'deleted'; uploadedBy, createdAt, price, downloadCount immutable. (6) BACKEND MODERATION APPROVAL — PROJECT_VAULT_ADDRESS may transition status pending->published; uploadedBy, createdAt, price, downloadCount immutable. (7) UPLOADER HIDE TOGGLE — the original uploader may toggle the 'hidden' flag while all other fields remain unchanged AND categorySetAt is PINNED unchanged. (8) UPLOADER EDIT — the original uploader may update title, price, and/or sizeBytes while all other fields remain unchanged AND categorySetAt is PINNED unchanged. (9) UPLOADER SOFT-DELETE — the original uploader (@data.uploadedBy) may transition status to 'deleted' while every other field (uploadedBy, createdAt, price, downloadCount, title, imageUrl, thumbnailUrl, category, mediaType, hidden, reviewedAt, reviewedBy, rejectionReason, categorySetAt) stays pinned/immutable. This replaces the old uploader hard-delete: soft-deleting keeps the record as a tombstone so buyer library entries (users/$userId/purchases/$wallpaperId) and purchase-verified download endpoints keep working instead of orphaning buyer references. Net effect: only the ADMIN edit path and creates can set/change categorySetAt; downloads, hide toggles, uploader edits, resubmits, and soft-deletes cannot move it. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateWallpapers(wallpaperId: string, data: WallpapersRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`wallpapers/${wallpaperId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Wallpapers: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
   (Get Single Item)
 */
export async function getWallpapers(wallpaperId: string): Promise<WallpapersResponse | null> {
  try {
    const result = await get(`wallpapers/${wallpaperId}`);
    return result as WallpapersResponse | null;
  } catch (error) {
    console.error(`Error getting Wallpapers: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Wallpapers document. (
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
  )
 */
export function subscribeWallpapers(callback: (data: WallpapersResponse | null) => void, wallpaperId: string): Promise<() => Promise<void>> {
  return subscribe(`wallpapers/${wallpaperId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WallpapersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Wallpapers at path '${`wallpapers/${wallpaperId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Wallpapers items from collection wallpapers
 
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
  
 */
export async function getManyWallpapers(filter: string = ""): Promise<WallpapersResponse[]> {
  try {
    const results = await get("wallpapers", { prompt: filter });
    return Array.isArray(results) ? results as WallpapersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Wallpapers collection at path 'wallpapers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Wallpapers collection at wallpapers
 
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
  
 */
export function subscribeManyWallpapers(callback: (data: WallpapersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wallpapers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WallpapersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Wallpapers collection at path '${"wallpapers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Wallpapers items from collection wallpapers
 
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
  
 */
export async function getAllWallpapers(filter: string = ""): Promise<WallpapersResponse[]> {
  try {
    const results = await get("wallpapers", { prompt: filter });
    return Array.isArray(results) ? results as WallpapersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Wallpapers collection at path 'wallpapers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Wallpapers collection at wallpapers
 
  Read Operation Details: Anyone can read wallpapers publicly. All fields visible including status, price, moderation metadata, the optional 'hidden' flag, and the optional 'sizeBytes' field. NOTE: UI is expected to filter status == 'deleted' out of public catalogs (deleted is a tombstone set when a creator profile was deleted), and to filter hidden == true wallpapers out of public catalogs while keeping them visible to the original uploader in My Uploads — but reads still succeed so buyer library entries (users/$userId/purchases/$wallpaperId) keep resolving.
  
 */
export function subscribeAllWallpapers(callback: (data: WallpapersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wallpapers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WallpapersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Wallpapers collection at path '${"wallpapers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Wallpapers items in collection wallpapers.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countWallpapers(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("wallpapers", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Wallpapers at path 'wallpapers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Wallpapers items in collection wallpapers.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateWallpapers(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("wallpapers", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Wallpapers at path 'wallpapers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: One allowed path: (1) ADMIN — ADMIN_ADDRESS can hard-delete any wallpaper regardless of status, for moderation. The former UPLOADER SELF-DELETE path was REMOVED: uploaders can no longer hard-delete their own wallpapers because doing so would orphan buyer library references (users/$userId/purchases/$wallpaperId) and break purchase-verified downloads. Uploaders now soft-delete instead by transitioning status to 'deleted' via the new UPLOADER SOFT-DELETE update branch (update path 9), which keeps the record as a tombstone so buyer libraries and download endpoints keep working. Backend creator-profile deletion likewise transitions wallpapers to status='deleted' rather than hard-deleting.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteWallpapers(wallpaperId: string): Promise<boolean> {
  try {
    const succeeded = await set(`wallpapers/${wallpaperId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Wallpapers: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Wallpapers for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteWallpapers(wallpaperId: string): DocumentOperation {
  return { path: `wallpapers/${wallpaperId}`, document: null };
}


/**
 * Tracks unique wallpaper views. Each document ID is the viewer's wallet address, ensuring one view per user per wallpaper. The frontend queries this sub-collection to count total unique views.
 */

export interface WallpapersViewsRequest {
  viewer: AddressType;
  viewedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WallpapersViewsResponse {
  viewer: string;
  viewedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a WallpapersViews operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWallpapersViews(wallpaperId: string, viewId: string, data?: WallpapersViewsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `wallpapers/${wallpaperId}/views/${viewId}`, document: doc };
}

/**
 * Authenticated users can record a view. The viewer field must match the caller wallet address and is immutable after creation. The document ID ($viewId) MUST be the viewer's wallet address to enforce one view per user per wallpaper. viewedAt is a Unix timestamp in seconds set once on creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWallpapersViews(wallpaperId: string, viewId: string, data?: WallpapersViewsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`wallpapers/${wallpaperId}/views/${viewId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting WallpapersViews: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
   (Get Single Item)
 */
export async function getWallpapersViews(wallpaperId: string, viewId: string): Promise<WallpapersViewsResponse | null> {
  try {
    const result = await get(`wallpapers/${wallpaperId}/views/${viewId}`);
    return result as WallpapersViewsResponse | null;
  } catch (error) {
    console.error(`Error getting WallpapersViews: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single WallpapersViews document. (
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
  )
 */
export function subscribeWallpapersViews(callback: (data: WallpapersViewsResponse | null) => void, wallpaperId: string, viewId: string): Promise<() => Promise<void>> {
  return subscribe(`wallpapers/${wallpaperId}/views/${viewId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WallpapersViewsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to WallpapersViews at path '${`wallpapers/${wallpaperId}/views/${viewId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many WallpapersViews items from collection wallpapers/${wallpaperId}/views
 
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
  
 */
export async function getManyWallpapersViews(wallpaperId: string, filter: string = ""): Promise<WallpapersViewsResponse[]> {
  try {
    const results = await get(`wallpapers/${wallpaperId}/views`, { prompt: filter });
    return Array.isArray(results) ? results as WallpapersViewsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WallpapersViews collection at path 'wallpapers/${wallpaperId}/views': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WallpapersViews collection at wallpapers/${wallpaperId}/views
 
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
  
 */
export function subscribeManyWallpapersViews(callback: (data: WallpapersViewsResponse[]) => void, wallpaperId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`wallpapers/${wallpaperId}/views`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WallpapersViewsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WallpapersViews collection at path '${`wallpapers/${wallpaperId}/views`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all WallpapersViews items from collection wallpapers/${wallpaperId}/views
 
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
  
 */
export async function getAllWallpapersViews(wallpaperId: string, filter: string = ""): Promise<WallpapersViewsResponse[]> {
  try {
    const results = await get(`wallpapers/*/views`, { prompt: filter });
    return Array.isArray(results) ? results as WallpapersViewsResponse[] : [];
  } catch (error) {
    console.error(`Error getting WallpapersViews collection at path 'wallpapers/${wallpaperId}/views': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WallpapersViews collection at wallpapers/${wallpaperId}/views
 
  Read Operation Details: Anyone can read view records. Used to count unique views per wallpaper.
  
 */
export function subscribeAllWallpapersViews(callback: (data: WallpapersViewsResponse[]) => void, wallpaperId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`wallpapers/*/views`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WallpapersViewsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WallpapersViews collection at path '${`wallpapers/*/views`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count WallpapersViews items in collection wallpapers/${wallpaperId}/views.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countWallpapersViews(wallpaperId: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`wallpapers/${wallpaperId}/views`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting WallpapersViews at path 'wallpapers/${wallpaperId}/views': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on WallpapersViews items in collection wallpapers/${wallpaperId}/views.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateWallpapersViews(wallpaperId: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`wallpapers/${wallpaperId}/views`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating WallpapersViews at path 'wallpapers/${wallpaperId}/views': ${formatError(error)}`);
    return { value: 0 };
  }
}

